import { ArrowLeft, Plus, Navigation, Menu, Search } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getSubNavigationColumns } from "../components/columns/SubNavigationColumns";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSubNavigations } from "@/redux/features/subNavigation/subNavigationThunk";
import { callLoader } from "@/redux/Loader/actions";
import { DataTable } from "@/components/ui/table";
import { getNavigationDropdownList } from "@/redux/features/navigation/navigationThunk";
import { SelectInput } from "../components/FormControls";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import { getAccountTypeDropdownList } from "@/redux/features/accountType/accountTypeSlice";
import { getBaseNavigations } from "@/redux/features/baseNavigation/baseNavigationThunk";
import TablePagination from "@/components/common/TablePagination";
import AddSubNavigationModal from "../modals/AddSubNavigationModal";

const SubNavigationPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const accountTypeId = searchParams.get("accountTypeId") || "";
  const { baseNavigationId, navigationId } = useParams();

  const dispatch = useDispatch();

  const { loading, subNavigationList, pagination, error } = useSelector(
    (state) => state.subNavigations,
  );

  const accountTypes = useSelector((state) => state.accountType.accountTypes);
  const baseNavigations = useSelector(
    (state) => state.baseNavigations.baseNavigationlist,
  );
  const navigationDropdownList = useSelector(
    (state) => state.navigations.navigationDropdownList,
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [isEdit, setIsEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [selectedAccountType, setSelectedAccountType] = useState("");

  // ==========================================
  // FETCH DATA
  // ==========================================

  const fetchData = async () => {
    dispatch(callLoader(true));

    try {
      await dispatch(
        getSubNavigations({
          iPageNo: currentPage,
          iPageRecords: pageSize,
          SearchKeyword: debouncedSearch,
          AccountTypeId: accountTypeId,
          NavigationId: navigationId,
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(callLoader(false));
    }
  };
  useEffect(() => {
    fetchData();
  }, [
    dispatch,
    currentPage,
    pageSize,
    debouncedSearch,
    accountTypeId,
    navigationId,
  ]);

  useEffect(() => {
    if (accountTypeId && baseNavigationId) {
      dispatch(
        getNavigationDropdownList({
          AccountTypeId: accountTypeId,
          BaseNavigationId: baseNavigationId,
        }),
      );
    }
  }, [dispatch, accountTypeId, baseNavigationId]);

  // ==========================================
  // SEARCH DEBOUNCE
  // ==========================================

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!accountTypes?.length) {
      dispatch(getAccountTypeDropdownList());
    }
  }, [dispatch, accountTypes]);

  useEffect(() => {
    if (!baseNavigations?.length) {
      dispatch(
        getBaseNavigations({
          iPageNo: 1,
          iPageRecords: 1000,
        }),
      );
    }
  }, [dispatch, baseNavigations]);

  useEffect(() => {
    dispatch(
      getNavigationDropdownList({
        AccountTypeId: accountTypeId,
        BaseNavigationId: baseNavigationId,
      }),
    );
  }, [dispatch, accountTypeId, baseNavigationId]);

  const handleAccountTypeChange = (name, value) => {
    setSelectedAccountType(value);

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("accountTypeId", value);
      return params;
    });
  };

  const handleBaseNavigationChange = (name, value) => {
    navigate(
      `/admin/base_navigations/${value}/navigations/${navigationId}/sub_navigations?accountTypeId=${accountTypeId || ""}`,
      { replace: true },
    );
  };

  const handleNavigationChange = (name, value) => {
    navigate(
      `/admin/base_navigations/${baseNavigationId}/navigations/${value}/sub_navigations?accountTypeId=${accountTypeId || ""}`,
      { replace: true },
    );
  };

  const handleView = useCallback(
    (row) => {
      //   navigate(
      //     `/admin/base_navigations/${row.BaseNavigationId}/navigations/${row.NavigationId}/sub_navigations?accountTypeId=${accountTypeId}`,
      //   );
    },
    [navigate],
  );

  const handleEdit = useCallback((row) => {
    setIsEdit(true);
    setSelectedRow(row);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (row) => {
    try {
      console.log("Delete Navigation:", row);

      // Example:
      // await dispatch(
      //   deleteBaseNavigation(row.BaseNavigationId)
      // ).unwrap();

      // toast.success("Navigation deleted");
    } catch (error) {
      console.error(error);

      // toast.error("Failed to delete navigation");
    }
  }, []);

  // ==========================================
  // COLUMNS
  // ==========================================

  const columns = useMemo(
    () =>
      getSubNavigationColumns({
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDelete,
      }),
    [handleView, handleEdit, handleDelete],
  );
  console.log("Columns", columns);

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((col) => col.key),
  );

  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          visibleColumns.length === 0 || visibleColumns.includes(column.key),
      ),
    [columns, visibleColumns],
  );

  const handleClose = (open) => {
    setModalOpen(open);

    if (!open) {
      setSelectedRow(null);
      setIsEdit(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedRow(null);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
      <div className="space-y-4">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <button
              onClick={() =>
                navigate(
                  `/admin/base_navigations/${baseNavigationId}/navigations?accountTypeId=${accountTypeId || ""}`,
                )
              }
              className="mt-1 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Go Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Navigation className="w-7 h-7 text-cyan-500" />
                Sub Navigations
              </h1>

              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Manage sub-navigation links, URLs, display order, permissions,
                and account type mappings
              </p>
            </div>
          </div>

          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Sub Navigation
          </button>
        </div>
        {/* SEARCH & COLUMNS VISIBILITY */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative w-full md:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                placeholder="Search base module..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
              <span className="font-bold text-slate-900 dark:text-white">
                {pagination?.totalRecord ?? 0} module found
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <SelectInput
              name="accountTypes"
              value={selectedAccountType}
              onValueChange={handleAccountTypeChange}
              placeholder="All Account Type"
              className="!py-2"
              options={
                accountTypes?.map((item) => ({
                  value: item.VALUE,
                  label: item.TEXT,
                })) || []
              }
            />

            <SelectInput
              name="baseNavigation"
              value={Number(baseNavigationId)}
              onValueChange={handleBaseNavigationChange}
              placeholder="All Base Navigation"
              className="!py-2"
              options={
                baseNavigations?.map((item) => ({
                  value: item.BaseNavigationId,
                  label: item.BaseNavigationName,
                })) || []
              }
            />
            <SelectInput
              name="navigation"
              value={Number(navigationId)}
              onValueChange={handleNavigationChange}
              placeholder="All Base Navigation"
              className="!py-2"
              options={
                navigationDropdownList?.map((item) => ({
                  value: item.VALUE,
                  label: item.TEXT,
                })) || []
              }
            />

            <ColumnVisibilityDropdown
              columns={columns}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              storageKey="sub-navigations-table-columns"
            />
          </div>
        </div>
        {/* TABLE */}
        <DataTable
          data={subNavigationList}
          columns={filteredColumns}
          loading={loading}
          emptyText="No Navigation found"
          Icon={Menu}
        />
        {/* PAGINATION */}
        <div className="p-4 pt-0">
          <TablePagination
            currentPage={currentPage}
            totalPages={pagination?.totalPage || 1}
            totalRecords={pagination?.totalRecord || 0}
            currentRecord={pagination?.currentRecord || 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
        {/* MODALS */}
        <AddSubNavigationModal
          open={modalOpen}
          onOpenChange={handleClose}
          onSuccess={fetchData}
          isEdit={isEdit}
          editData={selectedRow}
        />
      </div>
    </div>
  );
};

export default SubNavigationPage;
